import { useState, useEffect, useMemo } from 'react';

import { usePolPrice } from './usePolPrice';
import { useSolPrice } from './useSolPrice';
import {
  savePriceToCache,
  getPriceFromCache,
  isCacheValid,
} from '../utils/priceCache';
import type { ChainKey } from '../config/chainStakeConfig';
import { CHAIN_STAKE_CONFIG } from '../config/chainStakeConfig';

export interface UsePriceConversionOptions {
  /** Chain whose native token's USD price drives the conversion */
  chain?: ChainKey;
  /** Amount in the chain's native token (e.g. POL or SOL) */
  amount?: number;
  /**
   * Legacy alias for `amount` when `chain === 'polygon'`.
   * Existing callers (EntryFeeSection, others) still pass `polAmount`.
   */
  polAmount?: number;
  debounceMs?: number;
}

export interface UsePriceConversionResult {
  usdValue: number | null;
  isLoading: boolean;
  isStale: boolean;
  isCached: boolean;
  error: Error | null;
  /** Live or cached USD price for 1 unit of the selected chain's native token */
  pricePerToken: number | null;
  /**
   * Legacy alias for `pricePerToken` when chain is Polygon.
   * Always populated when chain === 'polygon' for backward compatibility.
   */
  pricePerPol: number | null;
}

const DEFAULT_DEBOUNCE_MS = 250;

/**
 * Hook for converting a chain's native-token amount to USD with debounce.
 *
 * Multi-chain (E4.7): pass `{ chain, amount }`.
 * Legacy callers: pass `{ polAmount }` and the hook behaves like the
 * pre-multi-chain POL-only version.
 */
export const usePriceConversion = ({
  chain,
  amount,
  polAmount,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}: UsePriceConversionOptions): UsePriceConversionResult => {
  const resolvedChain: ChainKey = chain ?? 'polygon';
  const resolvedAmount = amount ?? polAmount ?? 0;
  const ticker = CHAIN_STAKE_CONFIG[resolvedChain].ticker;

  const polState = usePolPrice();
  const solState = useSolPrice();
  const tokenState = resolvedChain === 'solana' ? solState : polState;

  const { price, isLoading: priceLoading, isError, error } = tokenState;
  const [debouncedAmount, setDebouncedAmount] = useState<number>(resolvedAmount);

  // Reset debounced value immediately on chain change so the rate snaps to
  // the new chain's USD value instead of carrying stale POL math.
  useEffect(() => {
    setDebouncedAmount(resolvedAmount);
  }, [resolvedChain]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounce amount changes within the same chain
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAmount(resolvedAmount);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [resolvedAmount, debounceMs]);

  // Save live price to cache when available
  useEffect(() => {
    if (price != null && !isError) {
      savePriceToCache(price, ticker);
    }
  }, [price, isError, ticker]);

  // Determine effective price (live or cached fallback)
  const { effectivePrice, isCached } = useMemo(() => {
    if (price != null) {
      return { effectivePrice: price, isCached: false };
    }

    if (isError) {
      const cached = getPriceFromCache(ticker);
      if (isCacheValid(cached)) {
        return { effectivePrice: cached!.price, isCached: true };
      }
    }

    return { effectivePrice: null, isCached: false };
  }, [price, isError, ticker]);

  const usdValue = useMemo(() => {
    if (effectivePrice == null) return null;
    const safeAmount = Math.max(0, debouncedAmount);
    return safeAmount * effectivePrice;
  }, [effectivePrice, debouncedAmount]);

  const isStale = resolvedAmount !== debouncedAmount;
  const isLoading = priceLoading;

  const errorResult =
    isError && error
      ? error instanceof Error
        ? error
        : new Error(String(error))
      : null;

  return {
    usdValue,
    isLoading,
    isStale,
    isCached,
    error: errorResult,
    pricePerToken: effectivePrice,
    pricePerPol: resolvedChain === 'polygon' ? effectivePrice : null,
  };
};
